import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSessions, syncSession, clearSessionErrors } from '../actions/sessionActions';

/**
 * This is your testing component - like a control panel to test your backend connection
 * You'll use this to see if your Redux actions work properly
 */
const SessionTestComponent = () => {
  // Think of useDispatch as your "command sender" - it sends commands to Redux
  const dispatch = useDispatch();
  
  // Think of useSelector as your "data reader" - it reads data from Redux store
  const sessionData = useSelector(state => state.sessions);
  
  // Local state for our test session form
  const [testSession, setTestSession] = useState({
    sessionId: `test_session_${Date.now()}`, // Creates unique ID like "test_session_1722518400000"
    userId: 'test@example.com',
    startedAt: new Date().toISOString(), // Current time in format "2024-08-01T10:30:00.000Z"
    activity_duration: 300, // 5 minutes in seconds
    inactivity_duration: 120, // 2 minutes in seconds
    heartbeats: [] // Empty for now
  });

  // Function to fetch all sessions from backend
  const handleFetchSessions = () => {
    console.log('Fetching sessions from backend...');
    dispatch(getAllSessions());
  };

  // Function to test Redux without backend (temporary)
  const handleTestRedux = () => {
    console.log('Testing Redux with fake data...');
    // This simulates what would happen when backend returns data
    dispatch({
      type: 'GET_ALL_SESSIONS_SUCCESS',
      payload: {
        sessions: [
          {
            sessionId: 'fake_session_1',
            userId: 'test@example.com',
            startedAt: '2024-08-02T10:00:00.000Z',
            endedAt: '2024-08-02T11:30:00.000Z',
            activity_duration: 60,
            inactivity_duration: 30,
            heartbeats: []
          },
          {
            sessionId: 'fake_session_2', 
            userId: 'admin@example.com',
            startedAt: '2024-08-02T14:00:00.000Z',
            endedAt: null,
            activity_duration: 45,
            inactivity_duration: 15,
            heartbeats: []
          }
        ],
        events: [
          {
            eventId: 'fake_event_1',
            sessionId: 'fake_session_1',
            timestamp: '2024-08-02T10:15:00.000Z',
            details: 'User clicked button'
          }
        ]
      }
    });
  };

  // Function to send our test session to backend
  const handleSyncSession = () => {
    console.log('Sending session to backend...', testSession);
    dispatch(syncSession(testSession));
  };

  // Function to clear any errors
  const handleClearErrors = () => {
    dispatch(clearSessionErrors());
  };

  // Function to generate a new test session
  const generateNewTestSession = () => {
    setTestSession({
      ...testSession,
      sessionId: `test_session_${Date.now()}`,
      startedAt: new Date().toISOString()
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Session Backend Test Panel</h1>
      <p>Use this to test your backend connection. Think of it like a practice area!</p>
      
      <div style={{ padding: '10px', backgroundColor: '#fffacd', borderRadius: '5px', marginBottom: '20px' }}>
        <h4>Status des Tests:</h4>
        <p><strong>Frontend Redux:</strong> {sessionData ? 'Configuré' : 'Erreur'}</p>
        <p><strong>Backend Status:</strong> {sessionData.error ? '❌ Erreur' : sessionData.allSessions.length > 0 ? 'Connecté' : 'Pas encore testé'}</p>
        <p><strong>Sessions en mémoire:</strong> {sessionData.totalSessions}</p>
      </div>

      {/* Display current Redux state */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
        <h3>Current Redux State:</h3>
        <p><strong>Loading:</strong> {sessionData.isLoading ? 'Yes' : 'No'}</p>
        <p><strong>Syncing:</strong> {sessionData.isSyncing ? 'Yes' : 'No'}</p>
        <p><strong>Total Sessions:</strong> {sessionData.totalSessions}</p>
        <p><strong>Total Events:</strong> {sessionData.totalEvents}</p>
        {sessionData.error && <p style={{color: 'red'}}><strong>❌ Error:</strong> {sessionData.error}</p>}
        {sessionData.syncError && <p style={{color: 'red'}}><strong>❌ Sync Error:</strong> {sessionData.syncError}</p>}
      </div>

      {/* Action buttons */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Actions:</h3>
        <button 
          onClick={handleTestRedux} 
          style={{ margin: '5px', padding: '10px 15px', fontSize: '14px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px' }}
        >
          Test Redux (Fake Data)
        </button>
        
        <button 
          onClick={handleFetchSessions} 
          disabled={sessionData.isLoading}
          style={{ margin: '5px', padding: '10px 15px', fontSize: '14px' }}
        >
          {sessionData.isLoading ? 'Loading...' : 'Fetch All Sessions (Real Backend)'}
        </button>
        
        <button 
          onClick={handleSyncSession} 
          disabled={sessionData.isSyncing}
          style={{ margin: '5px', padding: '10px 15px', fontSize: '14px' }}
        >
          {sessionData.isSyncing ? 'Syncing...' : 'Send Test Session (Real Backend)'}
        </button>
        
        <button 
          onClick={generateNewTestSession}
          style={{ margin: '5px', padding: '10px 15px', fontSize: '14px' }}
        >
          Generate New Test Session
        </button>
        
        <button 
          onClick={handleClearErrors}
          style={{ margin: '5px', padding: '10px 15px', fontSize: '14px' }}
        >
          🧹 Clear Errors
        </button>
      </div>

      {/* Test session details */}
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>Test Session Data:</h3>
        <pre style={{ backgroundColor: '#f8f8f8', padding: '10px', borderRadius: '3px', fontSize: '12px' }}>
          {JSON.stringify(testSession, null, 2)}
        </pre>
      </div>

      {/* Display fetched sessions */}
      {sessionData.allSessions.length > 0 && (
        <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
          <h3>Sessions from Backend:</h3>
          <pre style={{ backgroundColor: '#f8f8f8', padding: '10px', borderRadius: '3px', fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>
            {JSON.stringify(sessionData.allSessions, null, 2)}
          </pre>
        </div>
      )}

      {/* Display fetched events */}
      {sessionData.allEvents.length > 0 && (
        <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
          <h3>Events from Backend:</h3>
          <pre style={{ backgroundColor: '#f8f8f8', padding: '10px', borderRadius: '3px', fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>
            {JSON.stringify(sessionData.allEvents, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default SessionTestComponent;
